import { useMemo } from 'react';
import {
  IShipmentDetail,
  IShipmentDetailSettings,
  IShipmentHandlingUnitRow,
  IShipmentUnitOfMeasureSetting,
  IUnitValue
} from '../../../../../models/shipments/shipment-details.interface';

export type UnitSymbolMap = Record<string, string>;

// Joins a settings' UnitsOfMeasureDetails list into a `Unit` string ->
// symbol lookup, e.g. "MassUnit.Pound" -> "LB" - same key format
// (`${category}Unit.${unit}`) and uppercasing the reference Order app's
// useDetails.ts builds for its massUnits/lengthUnits maps, confirmed
// against a real settings response.
export function buildUnitSymbolMap(
  unitsOfMeasure: IShipmentUnitOfMeasureSetting[]
): UnitSymbolMap {
  const map: UnitSymbolMap = {};
  unitsOfMeasure.forEach(item => {
    if (!item.category || !item.unit || !item.symbol) return;
    map[`${item.category}Unit.${item.unit}`] = item.symbol.toUpperCase();
  });
  return map;
}

// `Unit` on the real API response is a string like "MassUnit.Pound"/
// "LengthUnit.Inch" (confirmed against a real /orders/shipments response -
// see shipment-grid.columns.tsx's sumHandlingUnits). Resolve its display
// symbol via the settings' UOM map first (e.g. "LB"), falling back to an
// explicit `Symbol` field if the API ever supplies one, then the raw enum's
// last segment as a last resort.
export function formatUnitSymbol(
  unit?: IUnitValue | null,
  unitSymbolMap: UnitSymbolMap = {}
): string {
  if (!unit) return '';
  if (unit.Unit && unitSymbolMap[unit.Unit]) return unitSymbolMap[unit.Unit];
  return unit.Symbol ?? unit.Unit?.split('.').pop() ?? '';
}

export function formatUnitValue(
  unit?: IUnitValue | null,
  unitSymbolMap: UnitSymbolMap = {}
): string {
  if (!unit || unit.Value === undefined || unit.Value === null) return '-';
  const symbol = formatUnitSymbol(unit, unitSymbolMap);
  return symbol ? `${unit.Value} ${symbol}` : `${unit.Value}`;
}

export function formatDimensions(
  row: IShipmentHandlingUnitRow,
  unitSymbolMap: UnitSymbolMap = {}
): string {
  const dimensions = row.dimensions;
  if (!dimensions) return '-';

  if (dimensions.shape === 2) {
    const diameter = dimensions.diameter;
    const length = dimensions.length;
    if (!diameter?.Value || !length?.Value) return '-';
    return `${diameter.Value}Dx${length.Value}L ${formatUnitSymbol(length, unitSymbolMap)}`;
  }

  const { length, width, height } = dimensions;
  if (!length?.Value || !width?.Value || !height?.Value) return '-';
  return `${length.Value}Lx${width.Value}Wx${height.Value}H ${formatUnitSymbol(length, unitSymbolMap)}`;
}

export function useDetailsTab(
  shipment: IShipmentDetail | undefined,
  settings: IShipmentDetailSettings
) {
  // One row per physical handling unit - each grouped `handlingUnits[i]`
  // (which carries a `quantity`) is expanded out and assigned the next
  // sequence number from `trackedHandlingUnits`, mirroring the reference
  // app's `mappedHandlingUnits` memo in useDetails.ts.
  const handlingUnitRows: IShipmentHandlingUnitRow[] = useMemo(() => {
    const rows: IShipmentHandlingUnitRow[] = [];
    let trackedIndex = 0;

    Object.values(shipment?.handlingUnits ?? {}).forEach(unit => {
      const quantity = unit.quantity || 1;
      for (let i = 0; i < quantity; i++) {
        rows.push({
          ...unit,
          sequenceNumber: shipment?.trackedHandlingUnits?.[trackedIndex]?.id
        });
        trackedIndex++;
      }
    });

    return rows;
  }, [shipment?.handlingUnits, shipment?.trackedHandlingUnits]);

  const totalQuantity = handlingUnitRows.length;

  const unitSymbolMap = useMemo(
    () => buildUnitSymbolMap(settings.unitsOfMeasure),
    [settings.unitsOfMeasure]
  );

  const totalWeightLabel = useMemo(() => {
    const totalsByUnit = handlingUnitRows.reduce<Record<string, number>>(
      (acc, row) => {
        const unit = row.weight?.Unit;
        if (!unit) return acc;
        acc[unit] = (acc[unit] ?? 0) + (row.weight?.Value ?? 0);
        return acc;
      },
      {}
    );

    return Object.entries(totalsByUnit)
      .map(([unit, total]) =>
        formatUnitValue({ Value: total, Unit: unit } as IUnitValue, unitSymbolMap)
      )
      .join(', ');
  }, [handlingUnitRows, unitSymbolMap]);

  const resolveHandlingUnitTypeName = (row: IShipmentHandlingUnitRow) =>
    settings.handlingUnitTypes.find(
      type => type.id === row.handlingUnitTemplateId
    )?.name ?? '-';

  const resolveNmfcClassName = (row: IShipmentHandlingUnitRow) =>
    settings.nmfcs.find(nmfc => nmfc.id === row.nmfcId)?.nmfcName ?? '-';

  const accessorialNames = useMemo(
    () =>
      (shipment?.accessorials ?? []).map(
        id =>
          settings.accessorials.find(accessorial => accessorial.id === id)
            ?.name ?? id
      ),
    [shipment?.accessorials, settings.accessorials]
  );

  return {
    handlingUnitRows,
    totalQuantity,
    totalWeightLabel,
    unitSymbolMap,
    resolveHandlingUnitTypeName,
    resolveNmfcClassName,
    accessorialNames
  };
}

export default useDetailsTab;
